import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { LayersOutlined } from '@mui/icons-material';
import {
  AppBar,
  Button,
  Portal,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OVERLAPPING_FEATURES_FOOTER_DESKTOP_MIN_HEIGHT_PX } from 'features/mapping/mapping.constants';
import { getSearchCardMinSnapPx } from '../../../../../constants';

export type OverlappingLayersNavFooterProps = {
  activeIndex: number;
  totalCount: number;
  onPrevious: () => void;
  onNext: () => void;
  /** When set, portals the footer into this element instead of `document.body`. */
  portalContainer?: HTMLElement | null;
};

export const OverlappingLayersNavFooter = (
  props: OverlappingLayersNavFooterProps,
): JSX.Element => {
  const { activeIndex, totalCount, onPrevious, onNext, portalContainer } =
    props;
  const { t } = useTranslation();

  const footer = (
    <AppBar
      position="fixed"
      color="transparent"
      elevation={0}
      sx={(theme) => ({
        bottom: 0,
        top: 'auto',
        left: 0,
        right: 0,
        zIndex: theme.zIndex.modal,
        pb: 'max(env(safe-area-inset-bottom), 8px)',
        pt: 1,
        minHeight: `${OVERLAPPING_FEATURES_FOOTER_DESKTOP_MIN_HEIGHT_PX}px`,
        backgroundColor: 'background.tertiary',
        boxShadow: theme.shadows[8],
        borderTop: `1px solid ${theme.palette.divider}`,
        [theme.breakpoints.down('tablet')]: {
          minHeight: getSearchCardMinSnapPx(),
        },
      })}
      component="footer"
    >
      <Toolbar disableGutters variant="dense">
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          flex={1}
          px={1}
        >
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              px: 1.5,
              backgroundColor: 'common.black',
              borderRadius: 2,
            }}
          >
            <LayersOutlined sx={{ color: 'common.white' }} />
            <Typography
              variant="body1"
              sx={{ color: 'common.white', fontWeight: 600 }}
            >
              {t('map.layers.activeItems', {
                count: activeIndex + 1,
                total: totalCount,
              })}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<ChevronLeftIcon />}
              onClick={onPrevious}
              color="surface"
              size="medium"
              sx={{ p: 1.5, fontWeight: 600 }}
            >
              {t('common.prev')}
            </Button>

            <Button
              endIcon={<ChevronRightIcon />}
              onClick={onNext}
              color="surface"
              size="medium"
              sx={{ p: 1.5, fontWeight: 600 }}
            >
              {t('common.next')}
            </Button>
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );

  return <Portal container={portalContainer || document.body}>{footer}</Portal>;
};
